import { captureException } from "@sentry/react";
import { getMsalInstanceAsync } from "../../core/auth/useAuth";
import { tokenScopes } from "@coworker/apprestructured/src/assets/auth/msalConfig";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { reportMessageToSentry } from "../useConfigureSentry";

const redirectGuardKey = "msal.redirected";

const hasRedirected = () => {
  try {
    return sessionStorage.getItem(redirectGuardKey) === "1";
  } catch {
    return false;
  }
};

const setRedirected = () => {
  try {
    sessionStorage.setItem(redirectGuardKey, "1");
  } catch {
    // Storage can be unavailable due to browser privacy settings.
  }
};

const clearRedirected = () => {
  try {
    sessionStorage.removeItem(redirectGuardKey);
  } catch {
    // Storage can be unavailable due to browser privacy settings.
  }
};

const getMsalToken = async () => {
  const msalInstance = await getMsalInstanceAsync();
  const accounts = msalInstance.getAllAccounts();

  const isCacheCorruptionError = (msg: string) =>
    msg.includes("Unexpected token") ||
    msg.includes("JSON") ||
    msg.includes("cache") ||
    msg.includes("parsing") ||
    msg.includes("unable_to_parse") ||
    msg.includes("crypto_key_not_found") ||
    msg.includes("key_not_found");

  if (!accounts[0]) {
    // Guard against redirect loops in the no-account path (e.g. repeated calls
    // before the redirect completes, or a failed redirect that left the flag set).
    if (hasRedirected()) {
      return null;
    }
    setRedirected();
    try {
      await msalInstance.acquireTokenRedirect({
        scopes: tokenScopes,
      });
      return null;
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "interaction_in_progress"
      ) {
        return null;
      } else {
        // Redirect failed — clear the flag so the next page load can retry.
        clearRedirected();
        reportMessageToSentry(
          `Error during acquireTokenRedirect.`,
          { accounts },
          "warning"
        );
        throw error;
      }
    }
  } else {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: tokenScopes,
        account: accounts[0] as AccountInfo,
      });
      // Successful silent token — safe to clear any lingering redirect guard.
      clearRedirected();
      return response.accessToken;
    } catch (error: any) {
      const message = error?.message || "";

      // Check and log to sentry if MSAL cache corruption, no action required atm.
      if (isCacheCorruptionError(message)) {
        reportMessageToSentry(
          `MSAL cache corruption detected`,
          { accounts, error: message },
          "error"
        );
        // Clear flag so the next attempt can retry auth from scratch.
        clearRedirected();
        return null;
      }

      // Only log refresh token expiry
      if (
        message.includes("AADSTS70008") ||
        message.includes("refresh token has expired") ||
        message.includes("inactivity")
      ) {
        reportMessageToSentry(
          `MSAL refresh token expired due to inactivity`,
          { accounts, error: message },
          "error"
        );
        // Clear flag — user will need to log in again manually.
        clearRedirected();
        return null;
      }

      // Only do interactive login if explicitly required
      if (error instanceof InteractionRequiredAuthError) {
        if (hasRedirected()) {
          return null;
        }
        setRedirected();
        try {
          await msalInstance.acquireTokenRedirect({
            scopes: tokenScopes,
          });
          return null;
        } catch (loginError) {
          // Redirect failed — clear the flag so the next page load can retry.
          clearRedirected();
          reportMessageToSentry(
            `Catch block, error during InteractionRequiredAuthError`,
            { accounts: accounts[0], error: loginError },
            "warning"
          );
          return null;
        }
      } else {
        captureException(error, {
          tags: { area: "auth", flow: "token_acquisition" },
          extra: { accounts },
        });
        // Clear flag on unexpected errors so auth can recover on next load.
        clearRedirected();
        return null;
      }
    }
  }
};

export async function getTokenAsync() {
  return await getMsalToken();
}
